// /mortgage-rates — Public Canadian mortgage rate comparison.
// Tuned for landlords (investment-property surcharges baked into the
// default view). Includes a Smart recommender powered by Mark.
import { useEffect, useMemo, useState } from "react";
import {
  TrendingUp, ArrowRight, MapPin, Sparkles, Loader2, RefreshCw,
  Building2, Wallet, ShieldCheck, Calendar, ChevronDown, CheckCircle2,
} from "lucide-react";
import PublicHeader from "@/components/layout/PublicHeader";

const PROVINCES = [
  ["", "All Canada"], ["ontario", "Ontario"], ["british-columbia", "British Columbia"],
  ["alberta", "Alberta"], ["quebec", "Quebec"], ["manitoba", "Manitoba"],
  ["saskatchewan", "Saskatchewan"], ["nova-scotia", "Nova Scotia"],
  ["new-brunswick", "New Brunswick"], ["newfoundland-and-labrador", "Newfoundland and Labrador"],
  ["prince-edward-island", "Prince Edward Island"], ["yukon", "Yukon"],
  ["northwest-territories", "Northwest Territories"], ["nunavut", "Nunavut"],
];

const PRODUCTS = [
  ["", "All terms"],
  ["5yr-fixed", "5-year fixed"],
  ["5yr-variable", "5-year variable"],
  ["3yr-fixed", "3-year fixed"],
  ["1yr-fixed", "1-year fixed"],
];

const CATEGORY_PILLS = {
  "big-6":        "bg-slate-100 text-slate-700",
  "monoline":     "bg-emerald-100 text-emerald-700",
  "credit-union": "bg-sky-100 text-sky-700",
  "digital":      "bg-violet-100 text-violet-700",
};

function useDocumentMeta({ title, description, canonical }) {
  useEffect(() => {
    if (title) document.title = title;
    if (description) {
      let m = document.querySelector('meta[name="description"]');
      if (!m) {
        m = document.createElement("meta");
        m.setAttribute("name", "description");
        document.head.appendChild(m);
      }
      m.setAttribute("content", description);
    }
    if (canonical) {
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", canonical);
    }
  }, [title, description, canonical]);
}

function JsonLd({ data }) {
  return (
    <script type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}/>
  );
}

export default function MortgageRates() {
  const [manifest, setManifest] = useState(null);
  const [rates, setRates] = useState([]);
  const [byProduct, setByProduct] = useState({});
  const [loading, setLoading] = useState(true);

  // Filters
  const [province, setProvince] = useState("");
  const [product, setProduct] = useState("");
  const [purpose, setPurpose] = useState("investment");

  useDocumentMeta({
    title: "Best Canadian Mortgage Rates for Landlords (Feb 2026) | Central Rentals",
    description: "Live comparison of Canadian mortgage rates for investment properties: 5-year fixed, 5-year variable, 3-year fixed. Big 6 banks, monolines, credit unions, and digital lenders — sorted lowest first.",
    canonical: "https://centralrentals.ca/mortgage-rates",
  });

  useEffect(() => {
    fetch(`/api/mortgage-rates/manifest`)
      .then((r) => r.json())
      .then(setManifest)
      .catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const qs = new URLSearchParams({ purpose });
    if (province) qs.set("province", province);
    if (product) qs.set("product", product);
    fetch(`/api/mortgage-rates?${qs.toString()}`)
      .then((r) => r.json())
      .then((d) => {
        setRates(d.rates || []);
        setByProduct(d.by_product || {});
      })
      .catch(() => { setRates([]); setByProduct({}); })
      .finally(() => setLoading(false));
  }, [province, product, purpose]);

  const jsonLd = manifest && {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Canadian Mortgage Rate Comparison for Landlords",
    serviceType: "Mortgage rate comparison",
    areaServed: { "@type": "Country", name: "Canada" },
    provider: { "@type": "Organization", name: "Central Rentals Canada", url: "https://centralrentals.ca" },
    description: "Compare investment-property mortgage rates across Big 6 banks, monoline lenders, credit unions, and digital lenders.",
  };

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="mortgage-rates-page">
      {jsonLd && <JsonLd data={jsonLd}/>}
      <PublicHeader/>

      {/* Hero */}
      <section className="bg-gradient-to-br from-emerald-50 via-white to-sky-50 border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-14 md:py-20">
          <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-emerald-700 inline-flex items-center gap-1.5">
            <TrendingUp className="w-3 h-3"/> Updated weekly · Feb 2026
          </div>
          <h1 className="font-display mt-3 text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.05] max-w-3xl">
            Best Canadian mortgage rates <span className="text-emerald-700">for landlords</span>.
          </h1>
          <p className="mt-5 text-base md:text-lg text-slate-700 max-w-2xl leading-relaxed">
            Investment-property rates across 15 Canadian lenders. Big 6 banks, monoline brokers, credit unions, and digital lenders — sorted lowest first. Surcharges for rental properties already baked in.
          </p>

          {/* BoC stat row */}
          {manifest?.boc && (
            <div className="mt-7 grid grid-cols-2 sm:grid-cols-4 gap-2 max-w-3xl" data-testid="mortgage-boc-row">
              <Stat label="BoC policy rate" value={`${manifest.boc.current_rate}%`} sub={manifest.boc.last_change}/>
              <Stat label="Prime" value={`${manifest.boc.prime_rate}%`} sub="bank-published"/>
              <Stat label="Lowest 5yr fixed (inv.)"
                value={manifest.best_investment ? `${manifest.best_investment.rate_pct}%` : "—"}
                sub={manifest.best_investment?.lender || ""}/>
              <Stat label="Next BoC meeting"
                value={(manifest.boc.next_meeting || "").replace(/\d{4}-/, "")}
                sub="watch this date"/>
            </div>
          )}
        </div>
      </section>

      {/* Recommender CTA */}
      <RecommenderPane/>

      {/* Filter bar + table */}
      <section className="py-10 md:py-14 bg-gradient-to-br from-emerald-50/40 via-white to-sky-50/40 border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-5 md:px-8">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
            <div>
              <div className="text-xs uppercase tracking-[0.22em] font-bold text-emerald-700">Compare</div>
              <h2 className="font-display mt-1 text-2xl sm:text-3xl font-bold tracking-tighter">
                Live rate table
              </h2>
            </div>
            <div className="flex items-center gap-2 flex-wrap" data-testid="mortgage-filters">
              <Select value={purpose} onChange={setPurpose} testid="mortgage-filter-purpose"
                options={[["investment", "Investment property"], ["owner_occ", "Owner-occupied"]]}/>
              <Select value={province} onChange={setProvince} testid="mortgage-filter-province"
                options={PROVINCES}/>
              <Select value={product} onChange={setProduct} testid="mortgage-filter-product"
                options={PRODUCTS}/>
            </div>
          </div>

          {loading && (
            <div className="py-10 text-center text-slate-500 text-sm flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin"/> Loading rates…
            </div>
          )}

          {!loading && (
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="mortgage-rates-table">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                  <tr>
                    <th className="text-left px-4 py-2.5">Lender</th>
                    <th className="text-left px-4 py-2.5">Type</th>
                    <th className="text-left px-4 py-2.5">Product</th>
                    <th className="text-right px-4 py-2.5">Rate</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rates.map((r, i) => (
                    <tr key={`${r.lender}-${r.product}-${i}`} className="hover:bg-emerald-50/30" data-testid={`mortgage-row-${i}`}>
                      <td className="px-4 py-2.5 font-semibold text-slate-900">{r.lender}</td>
                      <td className="px-4 py-2.5">
                        <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${CATEGORY_PILLS[r.category] || "bg-slate-100 text-slate-600"}`}>
                          {r.category_label}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-slate-700">{r.product.replace("yr-", "-year ")}</td>
                      <td className="px-4 py-2.5 text-right">
                        <span className={`font-display font-bold tabular-nums ${i === 0 ? "text-emerald-700 text-base" : "text-slate-900"}`}>
                          {r.rate_pct}%
                        </span>
                        {purpose === "investment" && r.investment_surcharge_bps > 0 && (
                          <div className="text-[9px] text-slate-400">+{r.investment_surcharge_bps} bps inv.</div>
                        )}
                      </td>
                    </tr>
                  ))}
                  {!rates.length && (
                    <tr><td colSpan={4} className="text-center py-8 text-slate-400 text-sm">No rates match these filters.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* By-product condensed cards (visible on wider screens for skim-readability) */}
          {!loading && Object.keys(byProduct).length > 1 && (
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="mortgage-by-product">
              {Object.entries(byProduct).slice(0, 4).map(([prod, rows]) => (
                <div key={prod} className="p-4 rounded-2xl bg-white border border-slate-200">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-700">{prod.replace("yr-", "-year ")}</div>
                  <div className="font-display text-3xl font-bold text-slate-900 tabular-nums mt-1">
                    {rows[0]?.rate_pct}%
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">{rows[0]?.lender}</div>
                  <div className="text-[11px] text-slate-400 mt-1">{rows.length} lenders quoted</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 md:py-20">
        <div className="max-w-3xl mx-auto px-5 md:px-8">
          <div className="text-xs uppercase tracking-[0.22em] font-bold text-emerald-700">Landlord FAQs</div>
          <h2 className="font-display mt-2 text-2xl sm:text-3xl font-bold tracking-tighter">
            Investment-mortgage essentials
          </h2>
          <div className="mt-6 space-y-3">
            {FAQS.map((f, i) => (
              <details key={i} className="p-4 rounded-xl border border-slate-200 bg-white group" data-testid={`mortgage-faq-${i}`}>
                <summary className="font-display font-bold cursor-pointer text-slate-900 flex items-start justify-between gap-3">
                  <span>{f.q}</span>
                  <ArrowRight className="w-4 h-4 text-emerald-600 flex-none mt-1 group-open:rotate-90 transition-transform"/>
                </summary>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div className="p-3 rounded-2xl border border-slate-200 bg-white/80 backdrop-blur" data-testid={`mortgage-stat-${label.toLowerCase().replace(/[^a-z]/g, "")}`}>
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700">{label}</div>
      <div className="font-display text-xl font-bold text-slate-900 mt-1 tabular-nums">{value}</div>
      {sub && <div className="text-[10px] text-slate-500 mt-0.5 truncate">{sub}</div>}
    </div>
  );
}

function Select({ value, onChange, options, testid }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="px-3 py-1.5 rounded-lg border border-slate-300 focus:border-emerald-500 outline-none text-xs bg-white font-semibold">
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
}

const FAQS = [
  { q: "Why are investment-property rates higher than owner-occupied?",
    a: "Lenders price rental mortgages 25-50 basis points above owner-occupied because tenants default at higher rates than homeowners and CMHC insurance is rarely available. Some monoline lenders specialize in rentals and offer the smallest surcharge." },
  { q: "Can I use rental income to qualify?",
    a: "Yes — most monoline lenders count 80-100% of expected rent toward your gross debt service ratio. Big 6 banks tend to count only 50-80%, so brokers often place rental deals with monoline lenders for better qualification." },
  { q: "Fixed or variable for a 2026 rental purchase?",
    a: "With the BoC in a cutting cycle, variable rates are biased to decline through 2026. But variable means payment swings — if cashflow margin is tight, fixed gives certainty. Use Mark's recommender below to model your specific scenario." },
  { q: "What's the minimum down payment for a rental property?",
    a: "20% on properties up to $1M (no CMHC for rentals), and 25-35% above $1M depending on lender. Some credit unions are flexible on this — they look at the deal economics not just the percentage." },
];

// ─── Smart recommender ───────────────────────────────────────────────────
function RecommenderPane() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const [price, setPrice] = useState("750000");
  const [down, setDown] = useState("150000");
  const [province, setProvince] = useState("ontario");
  const [term, setTerm] = useState("any");
  const [risk, setRisk] = useState("balanced");

  const run = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    setResult(null);
    try {
      const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
      const r = await fetch(`${API}/api/mortgage-rates/recommend`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          purchase_price: Number(price),
          down_payment: Number(down),
          province,
          purpose: "investment",
          term_preference: term,
          risk_appetite: risk,
          amortization_years: 25,
        }),
      });
      if (!r.ok) {
        // Detect HTML responses (e.g. Cloudflare 502) and swap for a
        // friendly message instead of dumping the raw error page into
        // the UI — used to render `<!DOCTYPE html>…` verbatim.
        const raw = await r.text();
        const isHtml = /^\s*<(!doctype|html|head|body|div)/i.test(raw);
        const friendly = isHtml
          ? (r.status === 502 || r.status === 503 || r.status === 504
              ? "Mortgage service is briefly unavailable — please try again in a moment."
              : `We couldn't reach the mortgage service (${r.status}). Try again shortly.`)
          : (raw.slice(0, 240) || `HTTP ${r.status}`);
        throw new Error(friendly);
      }
      setResult(await r.json());
    } catch (err) {
      setResult({ error: String(err?.message || err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="py-10 md:py-14 bg-white border-b border-slate-200">
      <div className="max-w-5xl mx-auto px-5 md:px-8">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] font-bold text-violet-700 inline-flex items-center gap-1.5">
              <Sparkles className="w-3 h-3"/> Mark&apos;s pick
            </div>
            <h2 className="font-display mt-1 text-2xl sm:text-3xl font-bold tracking-tighter">
              Tell Mark your scenario, get the top 3 rates
            </h2>
          </div>
          <button onClick={() => setOpen(!open)} data-testid="mortgage-recommender-toggle"
            className="inline-flex items-center gap-1 text-xs font-bold text-violet-700 hover:text-violet-900">
            {open ? "Hide" : "Open"} recommender <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`}/>
          </button>
        </div>

        {open && (
          <form onSubmit={run} className="bg-violet-50/40 border border-violet-200 rounded-2xl p-5 space-y-4" data-testid="mortgage-recommender-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              <Field label="Purchase price (CAD)" value={price} onChange={setPrice} type="number" testid="mortgage-rec-price"/>
              <Field label="Down payment (CAD)" value={down} onChange={setDown} type="number" testid="mortgage-rec-down"/>
              <SelectField label="Province" value={province} onChange={setProvince} testid="mortgage-rec-province"
                options={PROVINCES.filter(([v]) => v !== "")}/>
              <SelectField label="Term" value={term} onChange={setTerm} testid="mortgage-rec-term"
                options={[["any","Any"], ["5yr-fixed","5yr fixed"], ["5yr-variable","5yr variable"], ["3yr-fixed","3yr fixed"]]}/>
              <SelectField label="Risk appetite" value={risk} onChange={setRisk} testid="mortgage-rec-risk"
                options={[["conservative","Conservative"], ["balanced","Balanced"], ["aggressive","Aggressive"]]}/>
            </div>
            <div className="flex justify-end">
              <button type="submit" disabled={busy}
                data-testid="mortgage-rec-submit"
                className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-violet-600 text-white text-sm font-bold hover:bg-violet-700 disabled:opacity-60">
                {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Sparkles className="w-4 h-4"/>}
                Get Mark&apos;s recommendation
              </button>
            </div>
          </form>
        )}

        {busy && (
          <div className="mt-5 py-10 text-center text-slate-500 text-sm flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin"/> Mark is comparing 15 lenders…
          </div>
        )}

        {result && !busy && (
          <RecommenderResult result={result}/>
        )}
      </div>
    </section>
  );
}

function RecommenderResult({ result }) {
  if (result?.error) {
    return (
      <div className="mt-5 p-4 rounded-2xl bg-rose-50 border border-rose-200 text-sm text-rose-800" data-testid="mortgage-rec-error">
        {result.error}
      </div>
    );
  }
  if (!result?.recommendations) return null;
  return (
    <div className="mt-6 space-y-4" data-testid="mortgage-rec-result">
      <div className="p-4 rounded-2xl bg-emerald-50 border border-emerald-200">
        <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700">Mark&apos;s read</div>
        <div className="font-display text-lg font-bold text-slate-900 mt-1">{result.headline}</div>
        {result.monthly_market && (
          <p className="text-xs text-slate-700 mt-2 leading-relaxed">{result.monthly_market}</p>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {result.recommendations.map((r, i) => (
          <div key={i} className={`p-4 rounded-2xl border ${i === 0 ? "border-emerald-400 bg-emerald-50/30" : "border-slate-200 bg-white"}`} data-testid={`mortgage-rec-card-${i}`}>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="font-display font-bold text-sm text-slate-900">{r.lender}</div>
              {typeof r.fit_score === "number" && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 font-bold">fit {r.fit_score}</span>
              )}
            </div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mt-1">{r.product}</div>
            <div className="font-display text-3xl font-bold text-emerald-700 mt-2 tabular-nums">{r.rate_pct}%</div>
            {r.monthly_payment > 0 && (
              <div className="text-[11px] text-slate-600 mt-1">
                ${Number(r.monthly_payment).toLocaleString()}/mo
              </div>
            )}
            <p className="text-xs text-slate-700 mt-2 leading-relaxed">{r.why}</p>
            {r.caveats && (
              <p className="text-[11px] text-amber-700 mt-2 italic">{r.caveats}</p>
            )}
          </div>
        ))}
      </div>

      {Array.isArray(result.next_steps) && result.next_steps.length > 0 && (
        <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-600">Next steps</div>
          <ol className="mt-2 space-y-1 list-decimal list-inside text-sm text-slate-700">
            {result.next_steps.map((s, i) => <li key={i}>{s}</li>)}
          </ol>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, type, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600">{label}</label>
      <input type={type || "text"} value={value} onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-1 w-full px-2 py-1.5 rounded border border-slate-300 focus:border-violet-500 outline-none text-sm"/>
    </div>
  );
}
function SelectField({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="mt-1 w-full px-2 py-1.5 rounded border border-slate-300 focus:border-violet-500 outline-none text-sm bg-white">
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}
